<template>
    <div>
        <el-table class="w-full"
                  row-key="id"
                  :data="botsData"
                  stripe
                  data-testid="aloai-bots-table">
            <el-table-column type="expand" data-testid="aloai-bot-expand-column">
                <template v-slot="scope">
                    <div class="row align-items-top">
                        <div class="col-md-6">
                            <span class="text-greyish break-word" :data-testid="'aloai-bot-description-' + scope.row.id">
                                <span>{{ scope.row.description ?? '-' }}</span>
                            </span>
                        </div>
                        <div class="col-md-6 b-l">
                            <p :data-testid="'aloai-bot-created-by-' + scope.row.id">
                                <b>Created By:</b> <span>{{ scope.row.created_by ? (scope.row.created_by.name + ' - #' + scope.row.created_by.id) : '-' }}</span>
                            </p>
                            <p :data-testid="'aloai-bot-modified-by-' + scope.row.id">
                                <b>Last Modified By:</b> <span>{{ scope.row.modified_by ? (scope.row.modified_by.name + ' - #' + scope.row.modified_by.id) : '-' }}</span>
                            </p>
                        </div>
                    </div>
                </template>
            </el-table-column>

            <el-table-column
                label="Name"
                align="left"
                :min-width="150"
                data-testid="aloai-bot-name-column">
                <template v-slot="scope">
                    <div class="d-flex">
                        <el-tooltip
                            class="mr-4 align-self-center"
                            effect="dark"
                            :content="disableTooltipContent(scope.row)"
                            :disabled="canEditAgent(scope.row)"
                            placement="top"
                            :data-testid="'aloai-bot-enable-tooltip-' + scope.row.id">
                            <el-switch
                                v-model="scope.row.enabled"
                                active-color="#00BF50"
                                :disabled="!canEditAgent(scope.row)"
                                @change="updateBotEnabled(scope.row)"
                                :data-testid="'aloai-bot-enable-switch-' + scope.row.id"
                            />
                        </el-tooltip>
                        <div>
                            <router-link :to="canEditAgent(scope.row) ? {
                                    name: 'AloAi: Configure an agent',
                                    params: {bot_id: scope.row.id}
                                } : ''"
                                :data-testid="'aloai-bot-name-link-' + scope.row.id">
                                <div class="hover-lower-opacity">
                                    <p class="mb-0">
                                        <b>{{ scope.row.name }}</b>
                                    </p>
                                    <span>#{{ scope.row.id }}</span>
                                </div>
                            </router-link>
                        </div>
                    </div>
                </template>
            </el-table-column>

            <el-table-column
                width="100"
                label="Type"
                align="center"
                data-testid="aloai-bot-type-column">
                <template v-slot="scope">
                    <span class="ml-2 d-inline-block label blue" :data-testid="'aloai-bot-type-' + scope.row.id">
                        {{ scope.row.type_text }}
                    </span>
                </template>
            </el-table-column>

            <el-table-column
                width="150"
                label="Direction"
                align="left"
                data-testid="aloai-bot-direction-column">
                <template v-slot="scope">
                    <span :data-testid="'aloai-bot-direction-' + scope.row.id">{{ scope.row.direction_text }}</span>
                </template>
            </el-table-column>

            <el-table-column
                width="150"
                label="Model"
                align="left"
                data-testid="aloai-bot-model-column">
                <template v-slot="scope">
                    <span v-if="scope.row.type === AloAi.TYPE_TEXT" :data-testid="'aloai-bot-model-' + scope.row.id">{{ scope.row.model_human_readable_text }}</span>
                    <span v-else :data-testid="'aloai-bot-voice-model-' + scope.row.id">{{ scope.row.voice_model_human_readable_text }}</span>
                </template>
            </el-table-column>

            <el-table-column
                min-width="100"
                label="Lines"
                align="left"
                data-testid="aloai-bot-lines-column">
                <template v-slot="scope">
                    <a>
                        <span class="text-dark-greenish">
                            <span v-if="scope.row.campaigns && scope.row.campaigns.length === 1">
                                <router-link 
                                    :to="{ 
                                        name: 'Line Settings',
                                        params: { 
                                            campaign_id: scope.row.campaigns[0].id
                                        },
                                        query: {
                                            tab: 'routing-settings'
                                        }
                                    }"
                                    class="text-dark-greenish"
                                    :data-testid="'aloai-bot-line-link-' + scope.row.id">
                                    <span :data-testid="'aloai-bot-line-name-' + scope.row.id">{{ scope.row.campaigns[0]?.name }}</span>
                                    <br/>
                                    <span v-if="scope.row.campaigns[0]?.incoming_numbers && scope.row.campaigns[0].incoming_numbers.length" :data-testid="'aloai-bot-line-number-' + scope.row.id">
                                        {{ scope.row.campaigns[0].incoming_numbers[0]?.phone_number | fixPhone }}
                                    </span>
                                </router-link>
                            </span>
                            <span v-else :data-testid="'aloai-bot-lines-count-' + scope.row.id">
                                {{ scope.row.campaigns.length }} Lines
                            </span>
                        </span>
                    </a>
                </template>
            </el-table-column>

            <el-table-column
                align="left"
                label="Status"
                :min-width="40"
                data-testid="aloai-bot-status-column">
                <template v-slot="scope">
                    <span v-if="scope.row.enabled"
                          class="text-green"
                          :data-testid="'aloai-bot-status-running-' + scope.row.id">
                        <i class="fa fa-play-circle text-success mr-1"></i>
                        <span>Running</span>
                    </span>
                    <span v-else
                          class="text-red"
                          :data-testid="'aloai-bot-status-paused-' + scope.row.id">
                        <i class="fa fa-pause-circle text-warning mr-1"></i>
                        <span>Paused</span>
                    </span>
                </template>
            </el-table-column>

            <el-table-column
                label="Operations"
                align="center"
                width="180"
                fixed="right"
                data-testid="aloai-bot-operations-column">
                <template v-slot="scope">
                    <div class="col-12">
                        <router-link
                            :to="{
                                name: 'User Activity',
                                params: {user_id: scope.row.user_id}
                            }"
                            :data-testid="'aloai-bot-activity-link-' + scope.row.id">
                            <button class="btn btn-sm btn-block purplish" :data-testid="'aloai-bot-activity-btn-' + scope.row.id">
                                <i class="fa fa-signal pull-left"></i>
                                Activity
                            </button>
                        </router-link>
                    </div>
                    <div class="col-12 mt-1">
                        <router-link
                            :to="{
                                name: 'AloAi: Configure an agent',
                                params: {bot_id: scope.row.id}
                            }"
                            :data-testid="'aloai-bot-edit-link-' + scope.row.id"
                        >
                            <el-tooltip
                                :content="disableTooltipContent(scope.row)"
                                :disabled="canEditAgent(scope.row)"
                                placement="top"
                                :data-testid="'aloai-bot-edit-tooltip-' + scope.row.id"
                            >
                                <button
                                    class="btn btn-sm btn-block info"
                                    :disabled="!canEditAgent(scope.row)"
                                    :data-testid="'aloai-bot-edit-btn-' + scope.row.id"
                                >
                                    <i class="fa fa-pencil pull-left"></i>
                                    Edit
                                </button>
                            </el-tooltip>
                        </router-link>
                    </div>
                    <div class="col-12 mt-1">
                        <button class="btn btn-sm btn-block danger"
                                :data-testid="'aloai-bot-delete-btn-' + scope.row.id"
                                @click="handleDelete(scope.row)">
                            <i class="fa fa-trash pull-left"></i>
                            Delete
                        </button>
                    </div>
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import axios from 'axios'
import * as AloAi from '../../constants/aloai'
import { aloai_mixin } from '../../mixins'

const NOT_BUILD = 1
const BUILD_SUCCESS = 2
const BUILD_IN_PROGRESS = 3
const BUILD_FAILED = 4

export default {
    props: {
        bots: {
            required: false,
            default: []
        }
    },

    mixins: [aloai_mixin],

    data() {
        return {
            NOT_BUILD,
            BUILD_SUCCESS,
            BUILD_IN_PROGRESS,
            BUILD_FAILED,
            delete_bot_visible: false,
            bot: null,
            count: 0,
            export_disabled: false,
            delete_disabled: false,
            // Todo: implement both search and pagination
            search: '',
            filters: {
                page: 1,
                size: 5,
            },
            AloAi
        }
    },

    computed: {
        // Todo: implement search logic
        botsData() {
            if (!this.bots) {
                return this.bots
            }
            return this.bots.filter(data => !this.search || data.name.toLowerCase().includes(this.search.toLowerCase()))
        }
    },

    methods: {
        disableTooltipContent(bot) {
            if (bot.type === AloAi.TYPE_TEXT) {
                return 'Text agent feature is disabled'
            }
            
            if (bot.type === AloAi.TYPE_VOICE) {
                if (bot.direction === AloAi.DIRECTION_INBOUND) {
                    return 'Inbound voice agent feature is disabled'
                }

                if (bot.direction === AloAi.DIRECTION_OUTBOUND) {
                    return 'Outbound voice agent feature is disabled'
                }
            }
        },
        canEditAgent(bot) {
            if (bot.type === AloAi.TYPE_TEXT) {
                return this.isAloAiTextAgentsEnabled()
            }
            
            if (bot.type === AloAi.TYPE_VOICE) {
                if (bot.direction === AloAi.DIRECTION_INBOUND) {
                    return this.isAloAiInboundVoiceAgentsEnabled()
                }

                if (bot.direction === AloAi.DIRECTION_OUTBOUND) {
                    return this.isAloAiOutboundVoiceAgentsEnabled()
                }

                return this.isAloAiVoiceAgentsEnabled()
            }

            return true
        },

        updateBotEnabled(bot) {
            this.setLoading(true)

            // Get only required parameters
            let payload = _.pick(bot, ['enabled'])

            axios.patch(`/api/v1/aloai/bot/${bot.id}/toggle-active`, payload).then(res => {
                this.setLoading(false)
                this.$notify.success({
                    offset: 95,
                    title: 'AloAi Agent',
                    message: 'Agent updated successfully.',
                })
                this.$emit('bot-update', bot)
            }).catch(err => {
                console.log(err)
                this.setLoading(false)
                this.$emit('bot-update', bot)
                this.$root.handleErrors(err.response)
            })
        },

        handleEdit(row) {
            // Each row is a bot object
            this.bot = row
            // Goes to the Bot Settings page
            this.$router.push({
                name: 'AloAi: Configure an agent',
                params: {
                    bot_id: this.bot.id
                }
            })
        },

        handleDelete(row) {
            this.$emit('bot-delete', row.id)
        },

        setLoading(loading) {
            this.export_disabled = loading
            this.delete_disabled = loading
        }
    },
}
</script>
